// Libraries
import { VictoryChart, VictoryAxis, VictoryGroup, VictoryStack } from 'victory';
import { useBreakpointValue } from '@chakra-ui/react';
// Components
import { TickLabel } from '@atoms/index';
// Utils
import { summaryToChartData, numberOfVesselsToChartDimensions } from '@utils/index';
import { renderBars } from './BarChart.elements';
import { cii, colorsScaleOpaqued, gridLayoutStyle } from '@theme/victory';
import { barChartStylesMap } from './BarChart.breakpoints';
// Types
import { BarChartProps } from './BarChart.types';

/**
 * Bar chart component.
 *
 * @component
 * @param {BarChartProps} props - The props for the BarChart component.
 * @param {VesselDetail[]} props.barChartData - The data for the bar chart.
 * @returns {ReactElement} The rendered BarChart component.
 */
export const BarChart = ({ barChartData }: BarChartProps) => {
  const { ytd, eoy } = summaryToChartData(barChartData);
  const { offset, width } = numberOfVesselsToChartDimensions(barChartData.length);
  const labelStyle = useBreakpointValue(barChartStylesMap.labelStyle);
  const chartPadding = useBreakpointValue(barChartStylesMap.chartPadding);

  const tickFormat = (value: number) => (Number.isInteger(value) ? value : '');

  return (
    <VictoryChart
      theme={cii}
      padding={chartPadding}
      style={{
        parent: {
          touchAction: 'manipulation',
        },
      }}
    >
      {/* X Axis */}
      <VictoryAxis tickLabelComponent={<TickLabel />} />
      {/* Y Axis */}
      <VictoryAxis
        dependentAxis
        tickFormat={tickFormat}
        style={{
          ...gridLayoutStyle,
          tickLabels: labelStyle,
        }}
      />
      {/* Bar system group */}
      <VictoryGroup offset={offset} style={{ data: { width: width } }}>
        {/* Year to date bar */}
        <VictoryStack>{renderBars({ barData: ytd })}</VictoryStack>
        {/* End of year bar */}
        <VictoryStack colorScale={colorsScaleOpaqued}>{renderBars({ barData: eoy })}</VictoryStack>
      </VictoryGroup>
    </VictoryChart>
  );
};
